import { motion } from 'framer-motion';
import { Phone, Mail, MapPin } from 'lucide-react';
import { SOCIAL_LINKS } from '@/lib/constants';

// Instagram Icon
const InstagramIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

// WhatsApp Icon
const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export function BusinessCardPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        {/* Card */}
        <div className="bg-white shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-charcoal text-white px-8 py-10 text-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
            >
              <h1 className="font-display text-3xl font-bold tracking-tight mb-1">TURCO</h1>
              <div className="flex justify-center gap-[3px] text-white/80 mb-6">
                {'SANDALYE'.split('').map((char, i) => (
                  <span key={i} className="text-[0.6rem] font-semibold">
                    {char}
                  </span>
                ))}
              </div>
              <div className="w-16 h-[1px] bg-gold mx-auto mb-6" />
              <h2 className="text-xl font-medium">Nurullah Çolak</h2>
              <p className="text-gray-400 text-sm mt-1">Satış Yöneticisi</p>
            </motion.div>
          </div>

          {/* Contact Info */}
          <div className="px-8 py-8 space-y-4">
            <motion.a
              href="tel:+905537942161"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.4 }}
              className="flex items-center gap-4 p-4 bg-gray-50 hover:bg-gray-100 transition-colors group"
            >
              <div className="w-10 h-10 bg-charcoal text-white flex items-center justify-center">
                <Phone size={18} />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Telefon</p>
                <p className="text-charcoal font-medium group-hover:text-gray-600 transition-colors">
                  0553 794 21 61
                </p>
              </div>
            </motion.a>

            <motion.a
              href={SOCIAL_LINKS.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.4 }}
              className="flex items-center gap-4 p-4 bg-gray-50 hover:bg-green-50 transition-colors group"
            >
              <div className="w-10 h-10 bg-[#25D366] text-white flex items-center justify-center">
                <WhatsAppIcon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">WhatsApp</p>
                <p className="text-charcoal font-medium group-hover:text-green-600 transition-colors">
                  Mesaj Gönder
                </p>
              </div>
            </motion.a>

            <motion.a
              href="mailto:info@turcosandalye.com"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5, duration: 0.4 }}
              className="flex items-center gap-4 p-4 bg-gray-50 hover:bg-gray-100 transition-colors group"
            >
              <div className="w-10 h-10 bg-charcoal text-white flex items-center justify-center">
                <Mail size={18} />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">E-posta</p>
                <p className="text-charcoal font-medium group-hover:text-gray-600 transition-colors">
                  info@turcosandalye.com
                </p>
              </div>
            </motion.a>

            <motion.a
              href={SOCIAL_LINKS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6, duration: 0.4 }}
              className="flex items-center gap-4 p-4 bg-gray-50 hover:bg-pink-50 transition-colors group"
            >
              <div className="w-10 h-10 bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 text-white flex items-center justify-center">
                <InstagramIcon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Instagram</p>
                <p className="text-charcoal font-medium group-hover:text-pink-600 transition-colors">
                  @turcosandalye
                </p>
              </div>
            </motion.a>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.7, duration: 0.4 }}
              className="flex items-center gap-4 p-4 bg-gray-50"
            >
              <div className="w-10 h-10 bg-charcoal text-white flex items-center justify-center">
                <MapPin size={18} />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider">Adres</p>
                <p className="text-charcoal font-medium text-sm">
                  Gürdinç sk 24/A, Defterdar Mah, Eyüp, İstanbul
                </p>
              </div>
            </motion.div>
          </div>

          {/* Footer */}
          <div className="px-8 py-4 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-400">
              Premium Mobilya Üretimi | 1985'ten Bu Yana
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
